import { useState, type ReactNode } from "react";

import { useAutoplay } from "@aviary/components/InformationCarousel/useAutoplay";
import { ClientOnly } from "@shared/components/ClientOnly/ClientOnly";

import { ArrowButton, usePreviousNextButtons } from "../ArrowButton";
import type { EmblaCarouselType, EmblaViewportRefType } from "../emblaCarouselTypes";

import * as styles from "./SlideContainer.styles";

interface Props {
  children: ReactNode;
  emblaRef: EmblaViewportRefType;
  emblaApi: EmblaCarouselType;
  /** Specifies whether the carousel should loop infinitely
   *
   * @default false
   */
  isLooping?: boolean;
  /**
   * Specifies whether the carousel items should be full width
   *
   * @default false
   */
  isFullWidth?: boolean;
  /** Specifies the type of carousel
   *
   * @default "product"
   */
  type?: "product" | "information";
  /**
   * Specifies whether to hide the navigation arrows or not
   *
   * @default false
   */
  hideNavigationArrows?: boolean;
  /**
   * Show prev/next without requiring hover on the carousel (e.g. image modals, where the cursor can leave the overlay while the dialog stays open).
   *
   * @default false
   */
  alwaysShowNavigationArrows?: boolean;
}

/**
 * @deprecated DS4 component — a DS5 `Carousel` replacement is coming soon. Do not add new usages.
 */
const SlideContainer = ({
  children,
  emblaRef,
  emblaApi,
  isLooping = false,
  isFullWidth = false,
  hideNavigationArrows = false,
  alwaysShowNavigationArrows = false,
  type = "product",
}: Props) => {
  const [isArrowButtonFocused, setIsArrowButtonFocused] = useState(false);

  const { previousBtnDisabled, nextBtnDisabled, onPreviousBtnClick, onNextBtnClick } =
    usePreviousNextButtons(emblaApi);

  const { onButtonAutoplayClick } = useAutoplay(emblaApi);

  const isInformationCarousel = type === "information";

  const shadowStyles = [
    styles.shadowWrapper,
    !previousBtnDisabled && styles.showShadow.left,
    !nextBtnDisabled && styles.showShadow.right,
  ];

  const sliderStyles = [styles.slider.base, !isFullWidth && styles.slider.overflowItems];

  const slidesContainerStyles = [
    styles.slidesContainer.base,
    isLooping && styles.slidesContainer.looping,
    isFullWidth && styles.slidesContainer.fullWidthItems,
  ];

  const handlePrevButtonClick = () => {
    if (isInformationCarousel) return onButtonAutoplayClick(onPreviousBtnClick);

    return onPreviousBtnClick();
  };

  const handleNextButtonClick = () => {
    if (isInformationCarousel) return onButtonAutoplayClick(onNextBtnClick);

    return onNextBtnClick();
  };

  const renderArrowButtons = () => {
    if (hideNavigationArrows) return null;

    return (
      <ClientOnly>
        <ArrowButton
          type="previous"
          onClick={() => handlePrevButtonClick()}
          disabled={previousBtnDisabled}
          onFocus={() => setIsArrowButtonFocused(true)}
          onBlur={() => setIsArrowButtonFocused(false)}
          isFocused={isArrowButtonFocused}
          persistentVisible={alwaysShowNavigationArrows}
        />
        <ArrowButton
          type="next"
          onClick={() => handleNextButtonClick()}
          disabled={nextBtnDisabled}
          onFocus={() => setIsArrowButtonFocused(true)}
          onBlur={() => setIsArrowButtonFocused(false)}
          isFocused={isArrowButtonFocused}
          persistentVisible={alwaysShowNavigationArrows}
        />
      </ClientOnly>
    );
  };

  return (
    <div
      css={alwaysShowNavigationArrows ? styles.arrowNavWrapperPersistent : styles.arrowNavWrapper}
      tabIndex={0}
    >
      <div css={isFullWidth ? undefined : shadowStyles}>
        <div css={sliderStyles} ref={emblaRef}>
          <div css={slidesContainerStyles} data-testid="item-wrapper">
            {children}
          </div>
        </div>
      </div>
      {renderArrowButtons()}
    </div>
  );
};

export { SlideContainer };
